/**
 * DatePicker — on-brand, date-only picker shared across every Parkify surface.
 *
 * Replaces the native <input type="date">, whose calendar pop-up is styled by
 * the browser (Chrome/Safari/etc.) rather than Parkify. Value/onChange speak
 * the same "YYYY-MM-DD" wall-clock string the native input already used, so
 * swapping a call site is a drop-in change — no timezone conversion happens
 * here (that's only needed for combined date+time absolute instants; see
 * parkify-pay/ops's DateTimePicker for that case).
 *
 * Styling relies only on the global @parkify/brand-tokens custom properties
 * the host app already loads, matching SupportButton's portability contract.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react'

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

interface Props {
  /** "YYYY-MM-DD", or empty/undefined for "nothing selected". */
  value: string | null | undefined
  /** Receives a new "YYYY-MM-DD" string, or undefined when cleared. */
  onChange: (date: string | undefined) => void
  /** "YYYY-MM-DD" lower bound (inclusive). */
  min?: string
  /** "YYYY-MM-DD" upper bound (inclusive). */
  max?: string
  /** Show a Clear button and allow the empty state. */
  optional?: boolean
  /** Trigger text when nothing is selected. */
  placeholder?: string
  disabled?: boolean
}

const pad = (n: number) => String(n).padStart(2, '0')

function parseDate(date: string) {
  const [y, mo, day] = date.split('-').map(Number)
  return { y, mo0: mo - 1, day }
}

const buildDate = (y: number, mo0: number, day: number) => `${y}-${pad(mo0 + 1)}-${pad(day)}`

const todayLocal = () => {
  const d = new Date()
  return { y: d.getFullYear(), mo0: d.getMonth(), day: d.getDate() }
}

const triggerStyle: CSSProperties = {
  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--color-gray-300)',
  fontFamily: 'inherit', fontSize: 13, background: 'var(--color-white)',
  cursor: 'pointer', gap: 8, textAlign: 'left',
}
const popoverStyle: CSSProperties = {
  position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
  width: 240, background: 'var(--color-white)', borderRadius: 12,
  border: '1px solid var(--color-gray-200)', boxShadow: '0 12px 32px rgba(35,31,32,0.18)',
  padding: 12,
}
const navBtn: CSSProperties = {
  width: 28, height: 28, display: 'grid', placeItems: 'center', borderRadius: 8,
  border: 'none', background: 'transparent', cursor: 'pointer',
  color: 'var(--color-gray-700)', fontFamily: 'inherit', fontSize: 15,
}
const dayBase: CSSProperties = {
  height: 30, borderRadius: 8, border: 'none', background: 'transparent',
  fontFamily: 'inherit', fontSize: 12.5, color: 'var(--color-black)',
}
const presetBtn: CSSProperties = {
  padding: '5px 10px', borderRadius: 999, border: '1px solid var(--color-gray-300)',
  background: 'var(--color-white)', cursor: 'pointer', fontFamily: 'inherit',
  fontSize: 11, fontWeight: 700, color: 'var(--color-brand)',
}

const CalendarIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }} aria-hidden>
    <rect x="3" y="4.5" width="18" height="16" rx="2.5" stroke="var(--color-brand)" strokeWidth="1.8" />
    <path d="M3 9h18M8 2.5v4M16 2.5v4" stroke="var(--color-brand)" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
)

export function DatePicker({
  value, onChange, min, max, optional = false, placeholder = 'Select date', disabled = false,
}: Props) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const has = !!value
  const sel = has ? parseDate(value!) : null
  const today = todayLocal()
  const minParts = min ? parseDate(min) : null
  const maxParts = max ? parseDate(max) : null

  const [view, setView] = useState(() =>
    sel ? { y: sel.y, mo0: sel.mo0 } : { y: today.y, mo0: today.mo0 })

  useEffect(() => {
    if (open && sel) setView({ y: sel.y, mo0: sel.mo0 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const isOutOfRange = (y: number, mo0: number, day: number) => {
    const t = Date.UTC(y, mo0, day)
    if (minParts && t < Date.UTC(minParts.y, minParts.mo0, minParts.day)) return true
    if (maxParts && t > Date.UTC(maxParts.y, maxParts.mo0, maxParts.day)) return true
    return false
  }

  const pickDay = (day: number) => {
    if (isOutOfRange(view.y, view.mo0, day)) return
    onChange(buildDate(view.y, view.mo0, day))
    setOpen(false)
  }

  const prevMonth = () => setView(v => v.mo0 === 0 ? { y: v.y - 1, mo0: 11 } : { y: v.y, mo0: v.mo0 - 1 })
  const nextMonth = () => setView(v => v.mo0 === 11 ? { y: v.y + 1, mo0: 0 } : { y: v.y, mo0: v.mo0 + 1 })

  const daysInMonth = new Date(Date.UTC(view.y, view.mo0 + 1, 0)).getUTCDate()
  const firstDow = new Date(Date.UTC(view.y, view.mo0, 1)).getUTCDay()
  const cells: (number | null)[] = [
    ...Array<null>(firstDow).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ]

  const triggerText = has
    ? new Date(Date.UTC(sel!.y, sel!.mo0, sel!.day)).toLocaleDateString('en-US', {
        timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric',
      })
    : placeholder

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        style={{ ...triggerStyle, opacity: disabled ? 0.55 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
          <CalendarIcon />
          <span style={{
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            color: has ? 'var(--color-black)' : 'var(--color-gray-500)',
          }}>{triggerText}</span>
        </span>
        <span style={{ color: 'var(--color-gray-500)', fontSize: 10, flexShrink: 0 }}>▾</span>
      </button>

      {open && (
        <div style={popoverStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <button type="button" onClick={prevMonth} style={navBtn} aria-label="Previous month"
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-gray-100)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>‹</button>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-black)' }}>
              {MONTHS[view.mo0]} {view.y}
            </div>
            <button type="button" onClick={nextMonth} style={navBtn} aria-label="Next month"
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-gray-100)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>›</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 2 }}>
            {WEEKDAYS.map((d, i) => (
              <div key={i} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: 'var(--color-gray-600)', padding: '2px 0' }}>{d}</div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {cells.map((day, i) => {
              if (day === null) return <div key={`b${i}`} />
              const isSel = !!sel && sel.y === view.y && sel.mo0 === view.mo0 && sel.day === day
              const isToday = today.y === view.y && today.mo0 === view.mo0 && today.day === day
              const disabledDay = isOutOfRange(view.y, view.mo0, day)
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => pickDay(day)}
                  disabled={disabledDay}
                  style={{
                    ...dayBase,
                    cursor: disabledDay ? 'not-allowed' : 'pointer',
                    opacity: disabledDay ? 0.35 : 1,
                    fontWeight: isSel || isToday ? 700 : 500,
                    background: isSel ? 'var(--color-brand)' : 'transparent',
                    color: isSel ? 'var(--color-white)' : 'var(--color-black)',
                    boxShadow: !isSel && isToday ? 'inset 0 0 0 1.5px var(--color-brand-border)' : 'none',
                  }}
                  onMouseEnter={e => { if (!isSel && !disabledDay) e.currentTarget.style.background = 'var(--color-brand-light)' }}
                  onMouseLeave={e => { if (!isSel && !disabledDay) e.currentTarget.style.background = 'transparent' }}
                >{day}</button>
              )
            })}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12, alignItems: 'center' }}>
            <button type="button" style={presetBtn} onClick={() => { onChange(buildDate(today.y, today.mo0, today.day)); setOpen(false) }}>Today</button>
            {optional && (
              <button type="button" style={{ ...presetBtn, color: 'var(--color-gray-600)' }}
                onClick={() => { onChange(undefined); setOpen(false) }}>Clear</button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
